import { useMemo } from "react";
import {
  addDays,
  endOfWeek,
  format,
  isSameDay,
  isToday as isTodayFn,
  startOfWeek,
} from "date-fns";
import { Phone, Handshake } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRIORITY_DOT } from "@/components/tasks/priority";
import { formatTimeInTz, getDefaultTimezone } from "@/lib/timezones";
import type { CalendarEvent } from "@/api/calendar";

interface WeekViewProps {
  anchor: Date;
  events: CalendarEvent[];
  loading?: boolean;
  onCreateAt: (date: Date) => void;
  onOpenEvent: (event: CalendarEvent) => void;
  isDone: (key: string | null | undefined) => boolean;
}

const HOUR_START = 8;
const HOUR_END = 20;
const HOURS = HOUR_END - HOUR_START;
const PIXELS_PER_HOUR = 56;
const GUTTER_WIDTH_PX = 56;

interface TimedLayout {
  event: CalendarEvent;
  /** Minutes since midnight. */
  startMin: number;
  endMin: number;
  /** Column index inside an overlap cluster. */
  column: number;
  /** Total columns the cluster splits into. */
  columnCount: number;
}

interface DayLayout {
  date: Date;
  timed: TimedLayout[];
  allDay: CalendarEvent[];
}

export function WeekView({
  anchor,
  events,
  loading,
  onCreateAt,
  onOpenEvent,
  isDone,
}: WeekViewProps) {
  const days = useMemo(() => {
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    const out: Date[] = [];
    for (let i = 0; i < 7; i++) out.push(addDays(start, i));
    return out;
  }, [anchor]);

  const weekRange = useMemo(() => {
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    const end = endOfWeek(anchor, { weekStartsOn: 1 });
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }, [anchor]);

  // Per-day layout: split into "all-day" (no specific time) vs "timed"
  // with column packing for overlapping events.
  const layouts = useMemo<DayLayout[]>(() => {
    return days.map((d) => {
      const dayEvents = events
        .filter((e) => isSameDay(new Date(e.at), d))
        .sort((a, b) => a.at.localeCompare(b.at));
      const timed: TimedLayout[] = [];
      const allDay: CalendarEvent[] = [];
      for (const ev of dayEvents) {
        const placement = timedPlacementFor(ev);
        if (placement) {
          timed.push({
            event: ev,
            startMin: placement.startMin,
            endMin: placement.endMin,
            column: 0,
            columnCount: 1,
          });
        } else {
          allDay.push(ev);
        }
      }
      packColumns(timed);
      return { date: d, timed, allDay };
    });
  }, [days, events]);

  const hourLabels = useMemo(() => {
    const out: string[] = [];
    for (let h = HOUR_START; h <= HOUR_END; h++) {
      out.push(`${String(h).padStart(2, "0")}:00`);
    }
    return out;
  }, []);

  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      {/* Day headers */}
      <div
        className="grid border-b border-slate-900/[0.06] bg-slate-50"
        style={{ gridTemplateColumns: `${GUTTER_WIDTH_PX}px repeat(7, 1fr)` }}
      >
        <div className="border-r border-slate-200" />
        {days.map((d) => {
          const today = isTodayFn(d);
          return (
            <div
              key={d.toISOString()}
              className={cn(
                "border-r border-slate-200 px-3 py-2 text-left last:border-r-0",
                today && "bg-indigo-50/40",
              )}
            >
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                {format(d, "EEE")}
              </div>
              <div
                className={cn(
                  "mt-0.5 inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full text-sm tabular-nums",
                  today
                    ? "bg-indigo-600 px-1.5 font-semibold text-white"
                    : "font-medium text-slate-700",
                )}
              >
                {format(d, "d")}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day strip — events without a specific time (legacy tasks
       *  with date-only due_at, follow-ups, etc.). Capped at 3 visible
       *  per day so the strip can't push the time grid off-screen. */}
      {layouts.some((l) => l.allDay.length > 0) && (
        <div
          className="grid border-b border-slate-900/[0.06] bg-slate-50/40"
          style={{ gridTemplateColumns: `${GUTTER_WIDTH_PX}px repeat(7, 1fr)` }}
        >
          <div className="border-r border-slate-200 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-slate-400">
            All&nbsp;day
          </div>
          {layouts.map(({ date, allDay }) => (
            <div
              key={date.toISOString()}
              className="border-r border-slate-200 px-1 py-1 last:border-r-0"
            >
              <div className="flex flex-col gap-0.5">
                {allDay.slice(0, 3).map((ev) => (
                  <AllDayPill
                    key={ev.id}
                    event={ev}
                    isDone={isDone}
                    onClick={() => onOpenEvent(ev)}
                  />
                ))}
                {allDay.length > 3 && (
                  <span className="px-1 text-[10px] font-medium text-slate-500">
                    +{allDay.length - 3} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Time-axis grid */}
      <div
        className="relative grid"
        style={{
          gridTemplateColumns: `${GUTTER_WIDTH_PX}px repeat(7, 1fr)`,
          height: HOURS * PIXELS_PER_HOUR,
        }}
      >
        {/* Hour labels — left rail. The label sits at the top of its
         *  hour row so 09:00 lines up with the 9 AM grid line. */}
        <div className="relative border-r border-slate-200">
          {hourLabels.map((label, i) => (
            <div
              key={label}
              className="absolute left-0 right-0 text-right pr-2"
              style={{ top: i * PIXELS_PER_HOUR - 6 }}
            >
              <span className="text-[10px] font-medium tabular-nums text-slate-400">
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Day columns — relative-positioned so events can be absolutely
         *  placed within them. Hour grid lines are drawn via background
         *  gradient. */}
        {layouts.map(({ date, timed }) => {
          const today = isTodayFn(date);
          return (
            <DayColumn
              key={date.toISOString()}
              date={date}
              events={timed}
              today={today}
              hours={HOURS}
              hourPx={PIXELS_PER_HOUR}
              onCreateAt={onCreateAt}
              onOpenEvent={onOpenEvent}
              isDone={isDone}
            />
          );
        })}

        {loading && (
          <div className="pointer-events-none absolute inset-0 bg-white/40" />
        )}
      </div>
      <span className="sr-only" aria-live="polite">
        Week of {weekRange}
      </span>
    </div>
  );
}

interface DayColumnProps {
  date: Date;
  events: TimedLayout[];
  today: boolean;
  hours: number;
  hourPx: number;
  onCreateAt: (date: Date) => void;
  onOpenEvent: (event: CalendarEvent) => void;
  isDone: (key: string | null | undefined) => boolean;
}

function DayColumn({
  date,
  events,
  today,
  hours,
  hourPx,
  onCreateAt,
  onOpenEvent,
  isDone,
}: DayColumnProps) {
  // Click empty space → create at the corresponding hour. Compute the
  // hour from the click's offsetY so a click on the 14:30 line opens
  // the create dialog seeded to 14:30.
  function handleEmptyClick(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest("[data-event-card]")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const totalMinutes = (offsetY / hourPx) * 60;
    const startMin = HOUR_START * 60 + totalMinutes;
    // Snap to 15-minute slots so the seed time looks deliberate.
    const snapped = Math.round(startMin / 15) * 15;
    const seed = new Date(date);
    seed.setHours(0, 0, 0, 0);
    seed.setMinutes(snapped);
    onCreateAt(seed);
  }

  return (
    <div
      onClick={handleEmptyClick}
      className={cn(
        "relative border-r border-slate-200 last:border-r-0",
        today && "bg-indigo-50/30",
        "cursor-pointer transition-colors duration-150 hover:bg-indigo-500/[0.04]",
      )}
      style={{
        height: hours * hourPx,
        // Hour grid lines via repeating gradient — cheaper than mounting
        // <hr> per hour.
        backgroundImage:
          "linear-gradient(to bottom, transparent 0, transparent calc(100% - 1px), #e2e8f0 calc(100% - 1px), #e2e8f0 100%)",
        backgroundSize: `100% ${hourPx}px`,
        backgroundRepeat: "repeat-y",
      }}
    >
      {events.map(({ event, startMin, endMin, column, columnCount }) => {
        const top = ((startMin - HOUR_START * 60) / 60) * hourPx;
        const height = Math.max(
          18,
          ((endMin - startMin) / 60) * hourPx - 2,
        );
        // Side-by-side layout for overlapping events. Each event takes
        // 1/N of the column width, offset by its column index. A small
        // 2-px gutter keeps adjacent events visually separable.
        const widthPct = 100 / columnCount;
        const leftPct = column * widthPct;
        return (
          <TimedEventCard
            key={event.id}
            event={event}
            isDone={isDone}
            onClick={onOpenEvent}
            style={{
              position: "absolute",
              top,
              height,
              left: `calc(${leftPct}% + 2px)`,
              width: `calc(${widthPct}% - 4px)`,
            }}
          />
        );
      })}

      {/* Now-line for today */}
      {today && <NowIndicator hourPx={hourPx} />}
    </div>
  );
}

interface TimedEventCardProps {
  event: CalendarEvent;
  isDone: (key: string | null | undefined) => boolean;
  onClick: (event: CalendarEvent) => void;
  style: React.CSSProperties;
}

function TimedEventCard({
  event,
  isDone,
  onClick,
  style,
}: TimedEventCardProps) {
  const task = event.task;
  const done = isDone(task.status);
  const overdue = task.is_overdue && !done;
  const isCall = task.task_type === "call";
  const isMeeting = task.task_type === "meeting";
  const startDate = new Date(event.at);
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(startDate);
  const taskTz =
    task.scheduled_timezone && task.scheduled_timezone.length > 0
      ? task.scheduled_timezone
      : null;
  const viewerTz = getDefaultTimezone();
  const tooltip =
    taskTz && taskTz !== viewerTz
      ? `Scheduled at ${formatTimeInTz(startDate, taskTz)}`
      : task.title;

  return (
    <button
      type="button"
      data-event-card
      onClick={(e) => {
        e.stopPropagation();
        onClick(event);
      }}
      title={tooltip}
      style={style}
      className={cn(
        "flex flex-col overflow-hidden rounded-md border-l-[3px] bg-white px-1.5 py-1 text-left text-[11px] shadow-sm-tile transition-shadow duration-150 hover:shadow-plane-lift focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300",
        isCall
          ? "border-l-blue-400 bg-blue-50/50"
          : isMeeting
            ? "border-l-violet-400 bg-violet-50/50"
            : overdue
              ? "border-l-rose-400 bg-rose-50/50"
              : "border-l-slate-300",
        done && "opacity-50",
      )}
    >
      <div className="flex items-center gap-1">
        {isCall ? (
          <Phone className="h-3 w-3 shrink-0 text-blue-600" />
        ) : isMeeting ? (
          <Handshake className="h-3 w-3 shrink-0 text-violet-600" />
        ) : (
          <span
            className={cn(
              "h-1.5 w-1.5 shrink-0 rounded-full",
              PRIORITY_DOT[task.priority],
            )}
          />
        )}
        <span className="truncate font-mono text-[10px] tabular-nums text-slate-500">
          {time}
        </span>
      </div>
      <div
        className={cn(
          "truncate font-medium text-slate-800",
          done && "line-through",
          overdue && "text-rose-700",
        )}
      >
        {task.title}
      </div>
      {task.contact_name && (
        <div className="truncate text-[10px] text-slate-500">
          {task.contact_name}
        </div>
      )}
    </button>
  );
}

interface AllDayPillProps {
  event: CalendarEvent;
  isDone: (key: string | null | undefined) => boolean;
  onClick: () => void;
}

function AllDayPill({ event, isDone, onClick }: AllDayPillProps) {
  const task = event.task;
  const done = isDone(task.status);
  const overdue = task.is_overdue && !done;
  return (
    <button
      type="button"
      onClick={onClick}
      title={task.title}
      className={cn(
        "flex w-full items-center gap-1 rounded px-1 py-0.5 text-[10px] transition-colors hover:bg-slate-200/60",
        done && "opacity-50",
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          overdue ? "bg-rose-500" : PRIORITY_DOT[task.priority],
        )}
      />
      <span
        className={cn(
          "truncate",
          overdue ? "font-medium text-rose-700" : "text-slate-700",
          done && "line-through",
        )}
      >
        {task.title}
      </span>
    </button>
  );
}

function NowIndicator({ hourPx }: { hourPx: number }) {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const top = ((minutes - HOUR_START * 60) / 60) * hourPx;
  // Out of the visible range? Hide it.
  if (top < 0 || top > HOURS * hourPx) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-0 right-0 z-10 flex items-center"
      style={{ top }}
    >
      <span className="h-2 w-2 -translate-x-1 rounded-full bg-rose-500" />
      <span className="h-px flex-1 bg-rose-500" />
    </div>
  );
}

/** Decide whether an event has a precise time slot worth placing on
 *  the time axis. Calls / meetings always do (their scheduled_at
 *  drives the position); plain tasks land on the axis only when their
 *  due_at carries a non-midnight time. The rest go to the all-day
 *  strip. */
function timedPlacementFor(
  ev: CalendarEvent,
): { startMin: number; endMin: number } | null {
  const task = ev.task;
  const isEvent =
    task.task_type === "call" || task.task_type === "meeting";
  const startIso = task.scheduled_at ?? task.due_at;
  if (!startIso) return null;
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return null;
  const startMin = start.getHours() * 60 + start.getMinutes();
  if (!isEvent) {
    // Plain task at midnight (i.e. date-only due_at) goes to the
    // all-day strip; tasks scheduled at a real time get a 30-minute
    // slot on the axis so the user can see them in context.
    if (startMin === 0) return null;
    return { startMin, endMin: startMin + 30 };
  }
  const duration = task.duration_minutes ?? 30;
  return { startMin, endMin: startMin + duration };
}

/** Greedy column packing for overlapping events. Each event takes the
 *  lowest column index where it doesn't conflict with the previous
 *  event in that column, then we sweep again to compute the maximum
 *  column count *across* a connected overlap cluster so every event in
 *  the cluster shares the same width. */
function packColumns(events: TimedLayout[]) {
  if (events.length === 0) return;
  // Sort by start, ties broken by longer-first so nested events get
  // outer columns first.
  events.sort((a, b) =>
    a.startMin === b.startMin ? b.endMin - a.endMin : a.startMin - b.startMin,
  );

  // Pass 1 — assign a column to each event.
  const columnEnd: number[] = []; // running max-end per column
  for (const ev of events) {
    let placed = false;
    for (let c = 0; c < columnEnd.length; c++) {
      if (columnEnd[c] <= ev.startMin) {
        ev.column = c;
        columnEnd[c] = ev.endMin;
        placed = true;
        break;
      }
    }
    if (!placed) {
      ev.column = columnEnd.length;
      columnEnd.push(ev.endMin);
    }
  }

  // Pass 2 — group overlapping events into clusters and assign each
  // cluster its max column count. Two events overlap if their
  // [start, end] ranges intersect.
  let clusterStart = 0;
  let clusterMaxEnd = events[0].endMin;
  let clusterMaxCols = events[0].column + 1;
  for (let i = 1; i <= events.length; i++) {
    const ev = events[i];
    if (ev && ev.startMin < clusterMaxEnd) {
      clusterMaxEnd = Math.max(clusterMaxEnd, ev.endMin);
      clusterMaxCols = Math.max(clusterMaxCols, ev.column + 1);
      continue;
    }
    // Close cluster
    for (let j = clusterStart; j < i; j++) {
      events[j].columnCount = clusterMaxCols;
    }
    if (!ev) break;
    clusterStart = i;
    clusterMaxEnd = ev.endMin;
    clusterMaxCols = ev.column + 1;
  }
}
